import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Text } from '../../../core/design-system/components/Text';
import { Button } from '../../../core/design-system/components/Button';
import { Icon } from '../../../core/design-system/components/Icon';
import ClickableAvatar from '../ClickableAvatar';
import './styles.css';
import { useAuth } from '../../../core/providers/AuthProvider';
import { useParticipation } from '../../../features/posts/hooks/useParticipation';
import { useDeletePost } from '../../../features/posts/hooks/useDeletePost';
import { useShare } from '../../hooks/useShare';
import { useNotification } from '../../hooks/useNotification';
import { Modal } from '../../../core/design-system/components/Modal';
import { logger } from '../../../shared/utils/logger';
import { formatActivityDate } from '../../utils/date/DateFormatters';
import { useTranslation } from 'react-i18next';
import { getActivityTitle } from '../../utils/activity/getActivityTitle';
import { getFullDisplayLocation } from '../../utils/location/getDisplayLocation';

const ActivityDetails = ({ activity: initialActivity, ...props }) => {
  // État local pour l'activité pour pouvoir rafraîchir l'UI après participation
  const [activity, setActivity] = useState(initialActivity);
  const { user } = useAuth();
  const { t, i18n } = useTranslation(['common', 'activities', 'posts']);

  // Participation
  const {
    loading: loadingParticipation,
    // eslint-disable-next-line no-unused-vars
    error: participationError,
    toggleParticipation,
    isParticipating: isParticipatingFromHook
  } = useParticipation(activity, 
    // Calculate initial participation state - fix the logic to check array length
    (() => {
      const participants = (activity?.participants && activity.participants.length > 0) 
        ? activity.participants 
        : (activity?.activity?.participants || []);
      return participants.includes(user?.uid);
    })()
  );

  // Use the hook's state as the single source of truth
  const isParticipating = isParticipatingFromHook;
  
  // Ajout d'un check supplémentaire pour vérifier si l'activité est pleine
  const isActivityFull = useMemo(() => {
    // Utiliser la même logique que FeedCard pour la cohérence
    const currentCount = activity?.currentParticipants || activity?.activity?.currentParticipants || 0;
    const maxCount = activity?.activity?.maxParticipants || activity?.maxParticipants;
    
    // Si max défini et atteint, l'activité est pleine
    return maxCount && currentCount >= maxCount;
  }, [activity?.currentParticipants, activity?.activity?.currentParticipants, activity?.activity?.maxParticipants, activity?.maxParticipants]);

  // Mapping auteur
  const author = activity?.author;
  const authorName = author?.name || author?.displayName || 'Utilisateur';

  // Place formattedDate AVANT d'utiliser dateRelative
  // i18n.language nécessaire pour re-render au changement de langue
  const formattedDate = useMemo(() => {
    if (!activity?.activity?.date) return t('date_tbd');
    return formatActivityDate(activity.activity.date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.activity?.date, t, i18n.language]);

  const dateRelative = formattedDate;

  // Utilisateur courant
  const isOwner = user?.uid === author?.uid;
  
  // Memoize normalized participants and participantsDetails to avoid dependency warnings
  const rawParticipants = useMemo(() => {
    // Use the same logic as participation detection
    return (activity?.participants && activity.participants.length > 0) 
      ? activity.participants 
      : (activity?.activity?.participants || []);
  }, [activity?.participants, activity?.activity?.participants]);
  
  const rawParticipantsDetails = useMemo(() => {
    return (activity?.participantsDetails && activity.participantsDetails.length > 0)
      ? activity.participantsDetails
      : (activity?.activity?.participantsDetails || []);
  }, [activity?.participantsDetails, activity?.activity?.participantsDetails]);

  // Mise à jour des données à chaque changement d'activité
  useEffect(() => {
    if (initialActivity) {
      setActivity(initialActivity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialActivity, activity]);
  
  // Utilisation directe de la localisation normalisée
  const location = activity?.location;
  const lat = location?.lat ?? location?.location?.lat;
  const lng = location?.lng ?? location?.location?.lng;
  const hasCoords = typeof lat === 'number' && typeof lng === 'number';
  const mapSrc = hasCoords
    ? `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed&zoom=15&controls=0`
    : 'https://www.google.com/maps?q=48.866667,2.333333&z=15&output=embed&zoom=15&controls=0';
  const mapsUrl = hasCoords
    ? `https://www.google.com/maps?q=${lat},${lng}`
    : 'https://www.google.com/maps?q=48.866667,2.333333';
  // Utiliser la fonction utilitaire pour l'affichage
  const displayAddress = getFullDisplayLocation(location);

  // Suppression d'activité
  const {
    deletePost,
    loading: loadingDelete,
    error: deletePostError
  } = useDeletePost();

  // Partage
  const {isSharing } = useShare();
  const notification = useNotification('activity');

  // États locaux pour popins et menu
  const [showJoinConfirm, setShowJoinConfirm] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showReportConfirm, setShowReportConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Helper: get real participants (exclude author, only with uid and name/displayName)
  const getParticipantsToDisplay = () => {
    // Prefer details if available
    if (rawParticipantsDetails.length > 0) {
      return rawParticipantsDetails
        .filter(p => p.uid && p.uid !== author?.uid && (p.displayName || p.name));
    }
    // Fallback: use IDs only if no details (but don't show fake names)
    return rawParticipants
      .filter(uid => uid && uid !== author?.uid && uid !== user?.uid) // only real others
      .map(uid => {
        // Try to find details for the user
        if (user?.uid === uid) {
          return {
            uid: user.uid,
            name: user.displayName || user.name || '',
            photoURL: user.photoURL,
            displayName: user.displayName || user.name || ''
          };
        }
        // If no details, skip (don't show fake names)
        return null;
      })
      .filter(Boolean);
  };

  // Participants to display everywhere (list and modal)
  const participantsToDisplay = useMemo(getParticipantsToDisplay, [rawParticipants, rawParticipantsDetails, author?.uid, user]);

  // Participants count for label (exclude author)
  const participantsInfo = useMemo(() => {
    // Utiliser la même logique que FeedCard pour la cohérence
    const count = activity?.currentParticipants || activity?.activity?.currentParticipants || 0;
    const maxCount = activity?.activity?.maxParticipants || activity?.maxParticipants;
    if (maxCount) {
      return t('activity.participants_max', { current: count, max: maxCount });
    }
    return t('activity.participants', { count });
  }, [activity?.currentParticipants, activity?.activity?.currentParticipants, activity?.activity?.maxParticipants, activity?.maxParticipants, t]);

  // Handler participer - modifié pour ne rien faire si déjà participant
  const handleParticipate = () => {
    if (!user) {
      // Non connecté : laisser le parent rediriger vers signup (ex: PublicActivityPage)
      props.onJoin?.();
      return;
    }
    if (isOwner) {
      // Les propriétaires peuvent voir la liste des participants
      setShowParticipantsModal(true);
    } else if (isParticipating) {
      // Si déjà participant, ne rien faire (bouton disabled)
      return;
    } else if (isActivityFull) {
      // Rien à faire si activité pleine
      return;
    } else {
      // Sinon, ouvrir la modale de confirmation pour rejoindre
      setShowJoinConfirm(true);
    }
  };
  
  // Handler pour ouvrir la modale des participants
  const handleViewParticipants = () => {
    setShowParticipantsModal(true);
  };
  
  // Handler partage (bouton)
  const shareLink = window.location.href.replace(/[?&]share=true/, '');

  const activityTitle = useMemo(
    () => getActivityTitle({ activity: activity?.activity, t, i18n }),
    [activity?.activity, t, i18n]
  );

  // Toujours une string pour interpolation ou affichage
  const activityTitleString = typeof activityTitle === 'object'
    ? activityTitle[i18n.language] || activityTitle.en
    : activityTitle;

  const activityTitleForShare = useMemo(
    () => getActivityTitle({ activity: activity?.activity, t, i18n }),
    [activity?.activity, t, i18n]
  );

  const activityTitleForShareString = typeof activityTitleForShare === 'object'
    ? activityTitleForShare[i18n.language] || activityTitleForShare.en
    : activityTitleForShare;

  const shareText = t('common:share.text', {
    author: authorName,
    activity: activityTitleForShareString,
    date: dateRelative
  });

  const handleShareAction = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: activity?.title || '',
          text: shareText,
          url: shareLink,
        });
        notification.success(t('activities:actions.share_success', 'Partage réussi !'));
      } catch (err) {
        notification.error(t('activities:actions.share_error', 'Erreur lors du partage'));
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareLink);
        notification.success(t('activities:actions.link_copied', 'Lien copié !'));
      } catch (err) {
        notification.error(t('activities:actions.share_error', 'Erreur lors du partage'));
      }
    }
  }, [activity?.title, notification, t, shareLink, shareText]);

  // Handler menu
  const handleMenu = () => setShowMenu((v) => !v);
  const handleLeave = () => {
    setShowMenu(false);
    setShowLeaveConfirm(true);
  };
  const handleReport = () => {
    setShowMenu(false);
    setShowReportConfirm(true);
  };
  const handleDelete = () => {
    setShowMenu(false);
    setShowDeleteConfirm(true);
  };

  // Handler pour supprimer l'activité
  const confirmDelete = async () => {
    if (!activity?.id || !user?.uid) {
      logger.error('Cannot delete activity: Missing activity ID or user ID', {
        activityId: activity?.id,
        userId: user?.uid
      });
      notification.error(t('posts:toast.delete_error'));
      return;
    }
    
    try {
      const result = await deletePost(activity.id, user.uid, true);
      
      if (result.success) {
        window.history.back();
      }
    } catch (error) {
      logger.error('Error deleting activity:', error);
    }
  };

  // New useEffect for autoShare
  const { autoShare } = props;
  const hasSharedRef = useRef(false);

  useEffect(() => {
    if (autoShare && activity?.id && !hasSharedRef.current) {
      hasSharedRef.current = true;
      setTimeout(() => {
        setShowShareModal(true);
      }, 400);
    }
  }, [autoShare, activity?.id]);

  // Nettoyer l'URL après ouverture auto
  useEffect(() => {
    if (showShareModal && window.location.search.includes('share=true')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('share');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, [showShareModal]);

  useEffect(() => {
    // DEBUG: log l'activité reçue
    // eslint-disable-next-line no-console
    console.log('ActivityDetails activity:', activity?.activity);
    // eslint-disable-next-line no-console
    console.log('ActivityDetails currentParticipants:', {
      'activity.currentParticipants': activity?.currentParticipants,
      'activity.activity.currentParticipants': activity?.activity?.currentParticipants,
      'final': activity?.currentParticipants || activity?.activity?.currentParticipants || 0
    });
  }, [activity?.activity, activity?.currentParticipants]);

  const getActionButtonClass = () => {
    const baseClass = 'activity-details__action-button';
    if (isParticipating) return `${baseClass} active secondary disabled`;
    if (isActivityFull) return `${baseClass} disabled`;
    return `${baseClass} primary`;
  };

  return (
    <div className="activity-details">
      {/* Mini Map (bouton retour déplacé dans la navbar) */}
      <div className="activity-details__map-container">
        <div className="activity-details__map-placeholder" style={{ height: '200px' }}>
          <iframe
            title="map"
            width="100%"
            height="100%"
            style={{ border: 0, borderRadius: 'var(--border-radius-md)', pointerEvents: 'none' }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src={mapSrc}
            tabIndex={-1}
          />
        </div>
      </div>

      {/* Adresse complète sous la map avec icône itinéraire à droite */}
      <div className="activity-details__content">
        <div className="activity-details__location-row">
          <Icon name="map-pin" size={18} />
          <Text variant="body" className="activity-details__location-text">
            {displayAddress}
          </Text>
          <button
            className="activity-details__directions-icon"
            title={t('activities:actions.directions', 'Itinéraire')}
            aria-label={t('common:activityDetails.openMaps', 'Ouvrir dans Google Maps')}
            onClick={() => window.open(mapsUrl, '_blank', 'noopener,noreferrer')}
            type="button"
          >
            <Icon name="map" size={24} />
          </button>
        </div>

        {/* Avatar créateur + nom */}
        <div className="activity-details__creator-section">
          <div className="activity-details__creator-info">
            <ClickableAvatar 
              user={author}
              size="md"
              className="activity-details__creator-avatar"
            />
            <div className="activity-details__creator-details">
              <Text variant="subtitle" className="activity-details__creator-name">
                {authorName}
              </Text>
            </div>
          </div>
        </div>

        {/* CTAs alignés horizontalement */}
        <div className="activity-details__actions">
          <Button
            className={getActionButtonClass()}
            variant={isParticipating ? 'secondary' : 'primary'}
            aria-label={isParticipating ? t('posts:interactions.participating', 'Vous participez déjà') : t('activityDetails.join', 'Participer à l\'activité')}
            disabled={isOwner || isParticipating || (isActivityFull && !isParticipating) || loadingParticipation}
            onClick={handleParticipate}
          >
            {loadingParticipation ? <Icon name="loader" size={18} className="spinner" /> : 
              isParticipating ? <Icon name="user-check" size={18} /> : 
              isActivityFull ? <Icon name="x-circle" size={18} /> :
              <Icon name="user-plus" size={18} />
            }
            {isParticipating ? t('posts:interactions.participating', 'Participant') : 
             isActivityFull ? t('activityDetails.full', 'Complet') :
             t('activityDetails.join', 'Join')}
          </Button>
          <Button
            className="activity-details__action-button secondary"
            variant="secondary"
            aria-label={t('activityDetails.share', 'Partager l\'activité')}
            onClick={handleShareAction}
            disabled={isSharing}
          >
            {isSharing ? <Icon name="loader" size={18} className="spinner" /> : <Icon name="share-2" size={18} />}
            {t('activityDetails.share', 'Share')}
          </Button>
          <div style={{ position: 'relative' }}>
            <Button
              className="activity-details__more-button secondary"
              variant="secondary"
              aria-label={t('common:actions.view_more', 'Plus d\'actions')}
              onClick={handleMenu}
            >
              <Icon name="more-horizontal" size={18} />
            </Button>
            {showMenu && (
              <div className="activity-details__menu-dropdown" role="menu" style={{ 
                zIndex: 10,
                backgroundColor: 'var(--color-background)',
                boxShadow: 'var(--shadow-lg)',
                border: '1px solid var(--color-border)'
              }}>
                {isOwner && (
                  <button 
                    onClick={handleDelete}
                    role="menuitem"
                    className="menu-item-delete"
                    style={{ color: 'var(--color-danger)' }}
                  >
                    <Icon name="trash-2" size={16} />
                    {t('posts:actions.delete', 'Supprimer l\'activité')}
                  </button>
                )}
                {isParticipating && !isOwner && (
                  <button 
                    onClick={handleLeave} 
                    role="menuitem"
                    style={{ color: 'var(--color-text)' }}
                  >
                    <Icon name="user-minus" size={16} />
                    {t('posts:interactions.leave', 'Annuler ma participation')}
                  </button>
                )}
                {!isParticipating && !isOwner && (
                  <button 
                    onClick={handleReport} 
                    role="menuitem"
                    style={{ color: 'var(--color-text)' }}
                  >
                    <Icon name="flag" size={16} />
                    {t('posts:interactions.report', 'Signaler')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Type/Sous-Type */}
        <div className="activity-details__main-title">
          <div className="activity-details__type-badge">
            <span className="activity-icon">🏃‍♂️</span>
            <span className="activity-label">{activityTitleString}</span>
          </div>
        </div>

        {/* Date/Heure/Lieu + bouton agenda - UPDATED */}
        <div className="activity-details__date-row">
          <Icon name="calendar" size={18} />
          <Text variant="body" className="activity-details__date-text">
            {formattedDate}
          </Text>
          <button className="activity-details__calendar-button" aria-label={t('activities:details.add_to_calendar', 'Ajouter au calendrier')}>
            <Icon name="calendar-plus" size={24} />
          </button>
        </div>

        {/* Participants + chevron voir plus */}
        <div className="activity-details__participants">
          <div className="activity-details__participants-header">
            <Icon name="users" size={18} />
            <Text variant="subtitle">{participantsInfo}</Text>
            <button 
              className="activity-details__view-more" 
              aria-label={t('activityDetails.allParticipants', 'Voir tous les participants')}
              onClick={handleViewParticipants}
            >
              <Icon name="chevron-right" size={16} />
            </button>
          </div>
          <div className="activity-details__participants-list">
            {participantsToDisplay.length > 0 ? (
              participantsToDisplay.slice(0, 3).map((participant) => (
                <ClickableAvatar 
                  key={participant.uid}
                  user={participant}
                  size="md" 
                  className="activity-details__participant-avatar" 
                />
              ))
            ) : (
              <Text variant="body" className="activity-details__no-participants">
                {t('activityDetails.no_participants', 'Aucun participant pour le moment')}
              </Text>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="activity-details__description">
          <h2>{t('activityDetails.description', 'Description')}</h2>
          <div className="activity-details__description-content">
            <Text variant="body">
              {activity?.content || activity?.activity?.content || activity?.description || activity?.activity?.description || t('activityDetails.no_description', 'No description available.')}
            </Text>
          </div>
        </div>
      </div>

      {/* Popin de confirmation pour participer */}
      <Modal
        isOpen={showJoinConfirm}
        onClose={() => setShowJoinConfirm(false)}
        title={t('posts:confirmations.join_activity.title')}
        size="sm"
      >
        <Modal.Content className="modal-content">
          <Text>{t('posts:confirmations.join_activity.message')}</Text>
        </Modal.Content>
        <Modal.Footer className="modal-footer">
          <button 
            className="button-secondary"
            onClick={() => setShowJoinConfirm(false)}
          >
            {t('posts:confirmations.join_activity.cancel')}
          </button>
          <button 
            className="button-primary"
            onClick={async () => {
              try {
                if (!activity?.id || !user?.uid) {
                  logger.error('Cannot join activity: Missing activity ID or user ID', {
                    activityId: activity?.id,
                    userId: user?.uid
                  });
                  notification.error(t('posts:toast.joined_activity_error'));
                  return;
                }
                
                const result = await toggleParticipation();
                
                if (result?.success) {
                  setShowJoinConfirm(false);
                  // Note: useParticipation hook already handles the state update
                } else {
                  logger.error('Failed to join activity:', result?.error);
                }
              } catch (error) {
                logger.error('Error in handleConfirmJoin:', error);
              }
            }}
            autoFocus
          >
            {loadingParticipation ? t('activities:participation.loading') : t('posts:confirmations.join_activity.confirm')}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Popin annulation événement */}
      <Modal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        title={t('activities:confirmations.delete.title', "Annuler l'événement")}
        size="sm"
      >
        <Modal.Content className="modal-content">
          <Text>{t('activities:confirmations.delete.message', "Êtes-vous sûr de vouloir annuler cet événement ?")}</Text>
          <Text variant="body" style={{ color: 'var(--color-text-muted)', marginTop: 8 }}>
            {t('activities:confirmations.delete.message', "Cette action est irréversible et tous les participants seront notifiés.")}
          </Text>
        </Modal.Content>
        <Modal.Footer className="modal-footer">
          <button 
            className="button-secondary"
            onClick={() => setShowCancelConfirm(false)}
          >
            {t('common:actions.back', 'Retour')}
          </button>
          <button 
            className="button-danger"
            onClick={() => {
              setShowCancelConfirm(false);
              notification.success(t('activities:actions.event_canceled', 'Événement annulé'));
            }}
            autoFocus
          >
            {t('activities:confirmations.delete.confirm', "Annuler l'événement")}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Popin annulation participation */}
      <Modal
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        title={t('posts:interactions.leave')}
        size="sm"
      >
        <Modal.Content className="modal-content">
          <Text>{t('posts:interactions.leave_message')}</Text>
        </Modal.Content>
        <Modal.Footer className="modal-footer">
          <button 
            className="button-secondary"
            onClick={() => setShowLeaveConfirm(false)}
          >
            {t('posts:confirmations.join_activity.cancel')}
          </button>
          <button 
            className="button-primary"
            onClick={async () => {
              try {
                if (!activity?.id || !user?.uid) {
                  logger.error('Cannot leave activity: Missing activity ID or user ID', {
                    activityId: activity?.id,
                    userId: user?.uid
                  });
                  notification.error(t('posts:toast.left_activity_error'));
                  return;
                }
                
                const result = await toggleParticipation();
                
                if (result?.success) {
                  // Note: useParticipation hook already handles the state update
                } else {
                  logger.error('Failed to leave activity:', result?.error);
                }
              } catch (error) {
                logger.error('Error canceling participation:', error);
              } finally {
                setShowLeaveConfirm(false);
              }
            }}
            autoFocus
          >
            {loadingParticipation ? 
              <><Icon name="loader" size={18} className="spinner" /> {t('common:loading')}</> : 
              t('posts:interactions.leave')}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Popin signalement */}
      <Modal
        isOpen={showReportConfirm}
        onClose={() => setShowReportConfirm(false)}
        title={t('posts:interactions.report')}
        size="sm"
      >
        <Modal.Content className="modal-content">
          <Text>{t('posts:interactions.report_message')}</Text>
          <Text variant="body" style={{ color: 'var(--color-text-muted)', marginTop: 8 }}>
            {t('posts:interactions.report_subtitle')}
          </Text>
        </Modal.Content>
        <Modal.Footer className="modal-footer">
          <button 
            className="button-secondary"
            onClick={() => setShowReportConfirm(false)}
          >
            {t('posts:confirmations.join_activity.cancel')}
          </button>
          <button 
            className="button-danger"
            onClick={() => {
              setShowReportConfirm(false);
              notification.success(t('activities:actions.report_sent'));
            }}
            autoFocus
          >
            {t('posts:interactions.report')}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Popin de confirmation pour suppression */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title={t('posts:delete.title', 'Supprimer l\'activité')}
        size="sm"
      >
        <Modal.Content className="modal-content">
          <Text>{t('posts:delete.confirmation', 'Êtes-vous sûr de vouloir supprimer cette activité ? Cette action est irréversible.')}</Text>
          {deletePostError && (
            <Text color="error" className="error-message" style={{ marginTop: 12, color: 'var(--color-danger)' }}>
              {deletePostError}
            </Text>
          )}
        </Modal.Content>
        <Modal.Footer className="modal-footer">
          <button 
            className="button-secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            {t('common:actions.cancel', 'Annuler')}
          </button>
          <button 
            className="button-danger"
            onClick={confirmDelete}
            autoFocus
            disabled={loadingDelete}
          >
            {loadingDelete ? <><Icon name="loader" size={18} className="spinner" /> {t('common:loading')}</> : t('posts:delete.delete', 'Supprimer')}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Modale participants */}
      <Modal
        isOpen={showParticipantsModal}
        onClose={() => setShowParticipantsModal(false)}
        title={t('posts:interactions.participants')}
        size="xs"
      >
        <Modal.Content className="modal-content">
          <div className="participants-list">
            {participantsToDisplay.length > 0 ? (
              participantsToDisplay.map(participant => (
                <div 
                  key={participant.uid}
                  className="participant-item"
                >
                  <ClickableAvatar 
                    user={participant}
                    size="sm" 
                  />
                  <Text className="text">
                    {participant.name || participant.displayName}
                    {participant.uid === user?.uid && <span className="current-user"> ({t('posts:interactions.you')})</span>}
                  </Text>
                </div>
              ))
            ) : (
              <Text className="no-participants">
                {t('posts:participants.empty')}
              </Text>
            )}
          </div>
        </Modal.Content>
        <Modal.Footer className="modal-footer">
          <button 
            className="button-secondary"
            onClick={() => setShowParticipantsModal(false)}
          >
            {t('posts:confirmations.join_activity.cancel')}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Modale de partage personnalisée */}
      <Modal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={t('activityDetails.share', 'Partager l\'activité')}
        size="sm"
      >
        <Modal.Content className="modal-content">
          <Text>{t('activityDetails.share_text', 'Partage cette activité avec tes amis !')}</Text>
          <div style={{ margin: '16px 0' }}>
            <input
              type="text"
              value={shareLink}
              readOnly
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginBottom: 8 }}
              onFocus={e => e.target.select()}
            />
          </div>
        </Modal.Content>
        <Modal.Footer className="modal-footer">
          <button
            className="button-primary"
            onClick={async () => {
              await handleShareAction();
              setShowShareModal(false);
            }}
            autoFocus
          >
            {t('activityDetails.share_native', 'Partager')}
          </button>
          <button className="button-secondary" onClick={() => setShowShareModal(false)}>
            {t('common:actions.cancel', 'Annuler')}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Style ajouté en interne pour les participants et le menu */}
      <style>{`
        .participants-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-height: 400px;
          overflow-y: auto;
          padding: 8px 0;
        }
        
        .participant-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px;
          border-radius: var(--border-radius-sm);
        }
        
        .participant-item:hover {
          background-color: var(--color-background-light);
        }
        
        .text {
          font-weight: 500;
        }
        
        .current-user {
          color: var(--color-success);
          font-size: 0.85em;
          font-weight: 500;
          margin-left: 4px;
        }
        
        .no-participants {
          text-align: center;
          padding: 24px 0;
          color: var(--color-text-muted);
        }
        
        .activity-details__menu-dropdown button {
          padding: 8px 12px;
          width: 100%;
          text-align: left;
          display: flex;
          align-items: center;
          gap: 8px;
          border: none;
          background: transparent;
          cursor: pointer;
          border-radius: var(--border-radius-sm);
        }
        
        .activity-details__menu-dropdown button:hover {
          background-color: var(--color-background-light);
        }
        
        .menu-item-delete:hover {
          background-color: var(--color-danger-light) !important;
        }
      `}</style>
    </div>
  );
};

export default ActivityDetails; 